import React, { useState, useEffect, useCallback } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { History, X, FileText, Search, CheckCircle2, Clock, PenLine, AlertTriangle, MinusCircle, Sunrise, Moon, ShieldCheck, ChevronDown } from "lucide-react";
import PdfViewer from "@/components/PdfViewer";

const CHECKLIST_OPTS = [
  { id: "", title: "All checklists" },
  { id: "opening", title: "Opening" },
  { id: "closing_boh", title: "Kitchen Closing" },
  { id: "closing_foh", title: "FOH Closing" },
  { id: "temp_log", title: "Temperature Log" },
];

const STATUS_OPTS = [
  { id: "", title: "All statuses" },
  { id: "completed", title: "Completed" },
  { id: "incomplete", title: "Incomplete" },
  { id: "not_started", title: "Not started" },
];

const STATUS_META = {
  completed: { label: "Completed", cls: "border-[#16A34A]/40 bg-[#16A34A]/10 text-[#16A34A]", Icon: CheckCircle2 },
  incomplete: { label: "Incomplete", cls: "border-[#E5A100]/40 bg-[#E5A100]/10 text-[#E5A100]", Icon: AlertTriangle },
  not_started: { label: "Not started", cls: "border-[#E11D2A]/40 bg-[#E11D2A]/10 text-[#EF4444]", Icon: MinusCircle },
};

function fmtDay(d) {
  try { return new Date(d + "T00:00:00").toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }); }
  catch { return d; }
}

export function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.not_started;
  const Icon = m.Icon;
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${m.cls}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}

export function OnDutyLine({ onDuty }) {
  if (!onDuty || (!onDuty.day && !onDuty.night)) return null;
  return (
    <p className="text-[11px] text-[#6B6B6B] mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
      <span className="inline-flex items-center gap-1"><Sunrise className="h-3 w-3 text-[#E5A100]" /> Day: <span className="text-[#A3A3A3]">{onDuty.day || "—"}</span></span>
      <span className="inline-flex items-center gap-1"><Moon className="h-3 w-3 text-[#8B93FF]" /> Night: <span className="text-[#A3A3A3]">{onDuty.night || "—"}</span></span>
    </p>
  );
}

export function ItemBreakdown({ loading, detail }) {
  if (loading) return <p className="text-xs text-[#6B6B6B] mt-3 pt-3 border-t border-[#242424]">Loading items…</p>;
  if (!detail) return null;
  const secs = (detail.sections || []).filter((sec) => sec.total > 0);
  return (
    <div className="mt-3 pt-3 border-t border-[#242424] space-y-3" data-testid="history-breakdown" onClick={(e) => e.stopPropagation()}>
      {secs.map((sec) => {
        const missing = sec.items.filter((it) => !it.checked);
        return (
          <div key={sec.id} data-testid={`breakdown-section-${sec.id}`}>
            <p className="text-xs font-semibold flex items-center gap-2 text-[#F5F5F5]">
              {sec.title}
              <span className={`font-mono text-[10px] ${sec.done === sec.total ? "text-[#16A34A]" : "text-[#E5A100]"}`}>{sec.done}/{sec.total}</span>
            </p>
            {missing.length === 0 ? (
              <p className="text-[11px] text-[#16A34A] mt-0.5 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> All items checked</p>
            ) : (
              <ul className="mt-1 space-y-1">
                {missing.map((it) => (
                  <li key={it.key} className="text-[11px] text-[#EF4444] flex items-start gap-1.5">
                    <span className="mt-[4px] h-1.5 w-1.5 rounded-full bg-[#EF4444] shrink-0" /> {it.text}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function HistoryModal({ onClose }) {
  const [filters, setFilters] = useState({ date_from: "", date_to: "", checklist_id: "", completed_by: "", signoff_name: "", status: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);

  const openDetail = async (s) => {
    if (openKey === s.id) { setOpenKey(null); setDetail(null); return; }
    setOpenKey(s.id); setDetail(null); setDetailBusy(true);
    try {
      const p = new URLSearchParams({ checklist_id: s.checklist_id, date: s.business_date, location_id: s.location_id || "" });
      const { data } = await api.get(`/history/breakdown?${p.toString()}`);
      setDetail(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setDetailBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const { data } = await api.get(`/history?${params.toString()}`);
      setRows(data.sessions || []);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, [filters]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []); // initial load only

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  // group rows by business_date (already sorted date desc)
  const groups = [];
  const idx = {};
  rows.forEach((r) => {
    if (!(r.business_date in idx)) { idx[r.business_date] = groups.length; groups.push({ date: r.business_date, items: [] }); }
    groups[idx[r.business_date]].items.push(r);
  });

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl border border-[#2E2E2E] bg-[#171717] max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#171717] flex items-center justify-between px-5 py-4 border-b border-[#2E2E2E] z-10">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2"><History className="h-5 w-5 text-[#E11D2A]" /> Shift History</h3>
          <button onClick={onClose} data-testid="history-close-button" className="h-8 w-8 rounded-lg hover:bg-[#232323] flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5">
          <p className="text-xs text-[#6B6B6B] mb-3">Every checklist for each day — completed, incomplete, or never started. Pick any dates within the last 30 days. Leave dates blank for the last 7 days.</p>
          {/* Filters */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B] mb-1">From</label>
              <input type="date" data-testid="history-date-from" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} className="w-full rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-2.5 py-2 text-sm outline-none focus:border-[#E11D2A]" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B] mb-1">To</label>
              <input type="date" data-testid="history-date-to" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} className="w-full rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-2.5 py-2 text-sm outline-none focus:border-[#E11D2A]" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B] mb-1">Checklist</label>
              <select data-testid="history-checklist-select" value={filters.checklist_id} onChange={(e) => set("checklist_id", e.target.value)} className="w-full rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-2.5 py-2 text-sm outline-none focus:border-[#E11D2A]">
                {CHECKLIST_OPTS.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B] mb-1">Status</label>
              <select data-testid="history-status-select" value={filters.status} onChange={(e) => set("status", e.target.value)} className="w-full rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-2.5 py-2 text-sm outline-none focus:border-[#E11D2A]">
                {STATUS_OPTS.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B] mb-1">Person</label>
              <input data-testid="history-completed-by" value={filters.completed_by} onChange={(e) => set("completed_by", e.target.value)} placeholder="checked off / finalized" className="w-full rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-2.5 py-2 text-sm outline-none focus:border-[#E11D2A]" />
            </div>
            <div className="flex items-end">
              <button data-testid="history-search-button" onClick={load} className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#E11D2A] text-white font-semibold px-3 py-2 text-sm hover:bg-[#B91C1C] transition-colors">
                <Search className="h-4 w-4" /> Search
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-4" data-testid="history-results">
            {loading ? (
              <p className="text-sm text-[#6B6B6B] py-8 text-center">Loading…</p>
            ) : groups.length === 0 ? (
              <p className="text-sm text-[#6B6B6B] py-8 text-center">No checklists match these filters.</p>
            ) : groups.map((g) => (
              <div key={g.date} data-testid={`history-day-${g.date}`}>
                <p className="text-[11px] font-mono uppercase tracking-widest text-[#6B6B6B] mb-2 sticky">{fmtDay(g.date)}</p>
                <div className="space-y-2">
                  {g.items.map((s) => (
                    <div key={s.id} data-testid="history-row" onClick={() => openDetail(s)}
                      className={`rounded-xl border bg-[#0E0E0E] px-4 py-3 cursor-pointer transition-colors ${openKey === s.id ? "border-[#E11D2A]/60" : "border-[#2E2E2E] hover:border-[#E11D2A]/40"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium flex items-center flex-wrap gap-2">
                            <StatusBadge status={s.status} />
                            <span>{s.checklist_title}{s.location_name ? ` · ${s.location_name}` : ""}</span>
                            <span className="font-mono text-xs text-[#6B6B6B]">{s.done}/{s.total}</span>
                          </p>
                          <OnDutyLine onDuty={s.on_duty} />
                          {s.status === "completed" && s.completed_by && (
                            <p className="text-xs text-[#6B6B6B] mt-1">Finalized by {s.completed_by}{s.override_note ? " · override" : ""}</p>
                          )}
                          {s.contributors && s.contributors.length > 0 && (
                            <p className="text-xs text-[#A3A3A3] mt-1"><span className="text-[#6B6B6B]">Checked off by:</span> {s.contributors.join(", ")}</p>
                          )}
                          {s.missing && s.missing.length > 0 && (
                            <p data-testid="history-missing" className="text-xs text-[#EF4444] mt-1"><span className="opacity-80">Still missing:</span> {s.missing.join(", ")}</p>
                          )}
                          {(s.signoffs || []).length > 0 && (
                            <p className="text-xs text-[#A3A3A3] mt-1 flex items-center gap-1.5"><PenLine className="h-3 w-3" /> {s.signoffs.filter(Boolean).join(", ")}</p>
                          )}
                          {s.handoff_note && <p className="text-xs text-[#E5A100] mt-1 italic">Handoff: “{s.handoff_note}”</p>}
                          <p className="text-[10px] text-[#6B6B6B] mt-1.5 flex items-center gap-1">
                            <ChevronDown className={`h-3 w-3 transition-transform ${openKey === s.id ? "rotate-180" : ""}`} /> {openKey === s.id ? "Hide items" : "Tap to see items by station"}
                          </p>
                        </div>
                        {s.session_id && (
                          <button data-testid={`history-pdf-${s.id}`} onClick={(e) => { e.stopPropagation(); setReport({ url: `/sessions/${s.session_id}/report`, title: `${s.checklist_title} · ${s.business_date}`, filename: `paiges-${s.checklist_title}-${s.business_date}.pdf`.replace(/\s+/g, "-").toLowerCase() }); }} className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-[#2E2E2E] bg-[#171717] px-3 py-2 text-xs font-medium hover:border-[#E11D2A]/60 transition-colors">
                            <FileText className="h-3.5 w-3.5 text-[#E11D2A]" /> PDF
                          </button>
                        )}
                      </div>
                      {openKey === s.id && <ItemBreakdown loading={detailBusy} detail={detail} />}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {report && <PdfViewer relUrl={report.url} title={report.title} filename={report.filename} onClose={() => setReport(null)} />}
    </div>
  );
}
